import { useQuery } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { api } from 'lib/request/api';
import type { FailedApiResponse } from './types';
import type {
  PerforationResponse,
  PerforationRow,
} from 'types/well-perforations';

/**
 * Query-key prefix for the Well Page perforations read — one cache entry per
 * (href, uwi). Scoped so a future invalidation can clear one well's entry
 * without touching other wells'.
 */
export const WELL_PERFORATIONS_QUERY_KEY = 'well-page:perforations' as const;

/**
 * The default order asked of the BE: shallowest Top Depth MD first, matching the
 * table's default sort. `top_depth` is a gold column name from the endpoint's
 * sort allow-list — not a display key — and the two sort params must be sent
 * together or the query 422s.
 */
const DEFAULT_SORT_ATTRIBUTE = 'top_depth';
const DEFAULT_SORT_DIRECTION = 'asc';

/** What {@link useWellPerforations} returns. */
export interface UseWellPerforations {
  /** Every perforation interval for the well (empty while loading or on error). */
  data: PerforationRow[];
  /** True while the read is in flight. */
  loading: boolean;
  /** The failed read, or null. Passed straight to `DataTable`'s `error` prop. */
  error: Error | null;
}

/**
 * The perforations query key. `href` leads so the key stays unique if the
 * resource graph ever scopes the endpoint per well.
 *
 * @param {string} href - The `resources.queries.wellPerforations` href.
 * @param {string} uwi - The well's 10-digit id.
 * @return {unknown[]} The React Query cache key.
 */
export const wellPerforationsQueryKey = (href: string, uwi: string) =>
  [WELL_PERFORATIONS_QUERY_KEY, href, uwi] as const;

// ky is configured with a prefixUrl, so request paths must not start with a slash;
// HATEOAS hrefs do — strip it (same helper the subsurface-explorer queries use).
const toPath = (href: string) => href.replace(/^\/+/, '');

/**
 * Turns a failed request into the message the table shows. The query endpoint 422s
 * with `{ errors: [{ message, details }] }` on a validation failure, so the per-field
 * `details` are surfaced in preference to ky's generic HTTPError text. Anything else
 * (timeout, network, malformed body) falls through to a generic message.
 *
 * @param {unknown} error - The thrown ky error.
 * @return {Promise<Error>} The error to reject the query with.
 */
async function toPerforationsError(error: unknown): Promise<Error> {
  if (!(error instanceof HTTPError)) {
    return error instanceof Error
      ? error
      : new Error('Failed to load perforations');
  }
  const body = (await error.response
    .json()
    .catch(() => null)) as FailedApiResponse | null;
  const first = body?.errors?.[0];
  if (first?.details && typeof first.details === 'object') {
    return new Error(
      Object.entries(first.details)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n')
    );
  }
  return new Error(first?.message || 'Failed to load perforations');
}

/**
 * Loads every perforation interval for a well — `POST /queries/well_perforations`,
 * keyed by the well's 10-digit uwi.
 *
 * ONE request, no page loop. The endpoint is unpaginated (INS-10982) and returns the
 * full per-well set, so the table can group / search / sort client-side — all of which
 * would be wrong computed over a single server page, since one completion's intervals
 * could straddle a page boundary. It's affordable precisely because the set is small
 * (p99 ~22 rows, max observed ~209): a per-well read, not a table scan.
 *
 * The BE is asked for the table's default order (Top Depth MD ascending), so rows
 * arrive pre-sorted and no client-side default-sort pass is needed. Grouping still
 * orders the groups themselves and each group's intervals.
 *
 * React Query owns the request: one cache entry per well, deduped across re-renders
 * and cancelled through the injected `signal`.
 *
 * @param {string} uwi - The well's 10-digit id; the query is disabled until it's present.
 * @param {string} resourceHref - The `resources.queries.wellPerforations` href from the
 *   wells resource graph; the query is disabled until the graph resolves it.
 * @return {UseWellPerforations} The full interval set, load state, and any error.
 */
export function useWellPerforations(
  uwi: string,
  resourceHref: string
): UseWellPerforations {
  const { data, isFetching, error } = useQuery<PerforationRow[], Error>({
    queryKey: wellPerforationsQueryKey(resourceHref, uwi),
    queryFn: async ({ signal }) => {
      // `wrap_parameters :query` + `params.accept(:query)` on the controller — the
      // body nests under `query`. Keys stay camelCase; rails-boost underscores them
      // at the boundary (sortAttribute → sort_attribute).
      try {
        const response = await api
          .post(toPath(resourceHref), {
            json: {
              query: {
                uwi,
                sortAttribute: DEFAULT_SORT_ATTRIBUTE,
                sortDirection: DEFAULT_SORT_DIRECTION,
              },
            },
            signal,
          })
          .json<PerforationResponse>();
        return response.results;
      } catch (requestError) {
        throw await toPerforationsError(requestError);
      }
    },
    // Nothing to read until the well and its resource graph have both resolved —
    // without this the table would fire a request against an empty href on first paint.
    enabled: Boolean(uwi) && Boolean(resourceHref),
    // 5 min — perforations change only on a Kafka ingest, so revisiting the well
    // shouldn't re-hit the database.
    staleTime: 300_000,
  });

  return {
    data: data ?? [],
    loading: isFetching,
    error: error ?? null,
  };
}
